import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

const STALLED_THRESHOLD_MS = 8_000;

type QualitySurface = 'hub' | 'rnc' | 'fvs';

type SurfaceCopyEntry = {
  eyebrow: string;
  title: string;
  description: string;
  nextStep: string;
  stalledTitle: string;
  stalledDescription: string;
};

const SURFACE_COPY_PT: Record<QualitySurface, SurfaceCopyEntry> = {
  hub: {
    eyebrow: 'Qualidade',
    title: 'Preparando o painel de qualidade',
    description: 'Buscando não conformidades abertas, vistorias vencidas, RFIs e obras com risco.',
    nextStep: 'Quando carregar, confira as obras com maior risco e abra a primeira linha que precisa de atenção.',
    stalledTitle: 'Qualidade ainda está preparando os dados',
    stalledDescription: 'A primeira carga cruza várias obras. Você pode tentar de novo ou voltar para a lista de projetos.',
  },
  rnc: {
    eyebrow: 'Qualidade · Não conformidades',
    title: 'Carregando registros de não conformidade',
    description: 'Buscando pendências abertas, vencidas e itens aguardando verificação nesta obra.',
    nextStep: 'Quando carregar, resolva primeiro as não conformidades vencidas ou crie uma nova ocorrência.',
    stalledTitle: 'As não conformidades demoraram mais do que o normal',
    stalledDescription: 'Pode ser conexão ou uma consulta mais pesada. Tente de novo sem perder o contexto da obra.',
  },
  fvs: {
    eyebrow: 'Qualidade · Vistorias',
    title: 'Carregando vistorias de qualidade',
    description: 'Buscando checklists, modelos, etapas da obra e vistorias pendentes.',
    nextStep: 'Quando carregar, preencha a primeira vistoria aberta ou crie uma nova.',
    stalledTitle: 'As vistorias ainda estão carregando',
    stalledDescription: 'Os checklists podem demorar na primeira abertura. Tente novamente ou volte para a obra.',
  },
};

const SURFACE_COPY_EN: Record<QualitySurface, SurfaceCopyEntry> = {
  hub: {
    eyebrow: 'Quality',
    title: 'Preparing the quality dashboard',
    description: 'Loading open non-conformances, overdue inspections, RFIs, and at-risk projects.',
    nextStep: 'Once loaded, check the highest-risk projects and open the first item that needs attention.',
    stalledTitle: 'Quality is still preparing the data',
    stalledDescription: 'The first load crosses multiple projects. You can try again or go back to the project list.',
  },
  rnc: {
    eyebrow: 'Quality · Non-conformances',
    title: 'Loading non-conformance records',
    description: 'Loading open, overdue, and pending-verification items for this project.',
    nextStep: 'Once loaded, resolve overdue non-conformances first, or create a new occurrence.',
    stalledTitle: 'Non-conformances took longer than expected',
    stalledDescription: 'It may be a connection issue or a heavier query. Try again without losing your project context.',
  },
  fvs: {
    eyebrow: 'Quality · Inspections',
    title: 'Loading quality inspections',
    description: 'Loading checklists, templates, project phases, and pending inspections.',
    nextStep: 'Once loaded, complete the first open inspection or create a new one.',
    stalledTitle: 'Inspections are still loading',
    stalledDescription: 'Checklists can take a moment on the first open. Try again or go back to the project.',
  },
};

interface QualityLoadingStateProps {
  surface: QualitySurface;
  onRetry?: () => void;
  className?: string;
}

export function QualityLoadingState({
  surface,
  onRetry,
  className,
}: QualityLoadingStateProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const copy = (isEn ? SURFACE_COPY_EN : SURFACE_COPY_PT)[surface];
  const navigate = useNavigate();
  const params = useParams();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setTimedOut(true), STALLED_THRESHOLD_MS);
    return () => window.clearTimeout(id);
  }, []);

  const fallbackPath = useMemo(() => {
    if (surface === 'hub') return '/projects';
    return params.id ? `/projects/${params.id}` : '/projects';
  }, [params.id, surface]);

  const retry = () => {
    setTimedOut(false);
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label={copy.title}
      className={cn(
        'min-h-[360px] space-y-4 rounded-xl border border-line bg-surface px-4 py-4 text-ink sm:px-5',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-[68ch]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-600">
            {copy.eyebrow}
          </p>
          <h2 className="mt-1 text-lg font-semibold tracking-tight">{copy.title}</h2>
          <p className="mt-1 text-sm leading-relaxed text-ink-muted">{copy.description}</p>
        </div>
        <span className="rounded-full border border-line bg-surface-2 px-3 py-1 text-[12px] font-medium text-ink-muted">
          {isEn ? 'Preparing' : 'Preparando'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
      <Skeleton className="h-44 w-full" />

      <div className="rounded-lg border border-line bg-surface-2 px-4 py-3">
        <p className="text-sm font-medium text-ink">{isEn ? 'Next step' : 'Próximo passo'}</p>
        <p className="mt-1 text-sm leading-relaxed text-ink-muted">{copy.nextStep}</p>
      </div>

      {timedOut ? (
        <div className="rounded-lg border border-warn/30 bg-warn/10 px-4 py-3">
          <p className="text-sm font-semibold text-ink">{copy.stalledTitle}</p>
          <p className="mt-1 text-sm leading-relaxed text-ink-muted">
            {copy.stalledDescription}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={retry}>
              {isEn ? 'Try again' : 'Tentar novamente'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate(fallbackPath)}>
              {isEn ? 'Go back to a safe place' : 'Voltar para um lugar seguro'}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

interface QualityErrorStateProps {
  surface: QualitySurface;
  onRetry: () => void;
  error?: unknown;
  className?: string;
}

export function QualityErrorState({
  surface,
  onRetry,
  error,
  className,
}: QualityErrorStateProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const copy = (isEn ? SURFACE_COPY_EN : SURFACE_COPY_PT)[surface];
  const detail = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;

  return (
    <section
      role="alert"
      className={cn(
        'rounded-xl border border-line bg-surface px-5 py-6 text-ink',
        className,
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-err">
        {copy.eyebrow}
      </p>
      <h2 className="mt-1 text-lg font-semibold tracking-tight">
        {isEn ? 'Could not load right now' : 'Não consegui carregar agora'}
      </h2>
      <p className="mt-2 max-w-[68ch] text-sm leading-relaxed text-ink-muted">
        {isEn
          ? 'Check your connection and try again. This screen prepares the next quality action, so we didn\'t leave an empty space in its place.'
          : 'Verifica a internet e tenta de novo. Esta tela prepara a próxima ação de Qualidade, então não deixei um espaço vazio no lugar.'}
      </p>
      <div className="mt-4">
        <Button variant="primary" onClick={onRetry}>
          {isEn ? 'Try again' : 'Tentar novamente'}
        </Button>
      </div>
      {detail ? (
        <details className="mt-4 max-w-[68ch] rounded-lg border border-line bg-surface-2 text-[13px]">
          <summary className="cursor-pointer select-none px-4 py-3 font-medium text-ink-muted hover:text-ink">
            {isEn ? 'Show technical details' : 'Mostrar detalhes técnicos'}
          </summary>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all px-4 pb-4 pt-1 font-mono text-[12px] text-ink-muted">
            {detail}
          </pre>
        </details>
      ) : null}
    </section>
  );
}
